import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function RegisterPage() {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    whatsapp: '',
    email: '',
    plan: '',
    broker: '',
    platform: 'MT4',
    account: '',
    server: '',
    balance: '',
    type: 'Real',
    market: 'Gold',
    kasango: 'Yes',
    kolt: 'Yes',
    lastTrade: '',
    date: '',
    time: ''
  });

  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [bookings, setBookings] = useState({});

  const timeSlots = [
    "09:00","09:10","09:20","09:30","09:40","09:50",
    "10:00","10:10","10:20","10:30","10:40","10:50",
    "11:00","11:10","11:20","11:30","11:40","11:50",
    "12:00","12:10","12:20","12:30","12:40","12:50",
    "13:00","13:10","13:20","13:30","13:40","13:50",
    "14:00","14:10","14:20","14:30","14:40","14:50",
    "15:00","15:10","15:20","15:30","15:40","15:50",
    "16:00","16:10","16:20","16:30","16:40","16:50"
  ];

  // Load bookings from localStorage on mount
  useEffect(() => {
    const storedBookings = JSON.parse(localStorage.getItem("kasangoBookings")) || {};
    setBookings(storedBookings);
  }, []);

  // Update available time slots when date changes
  useEffect(() => {
    if (formData.date) {
      const bookedTimes = bookings[formData.date] || [];
      const available = timeSlots.filter(time => !bookedTimes.includes(time));
      setAvailableTimeSlots(available);
    } else {
      setAvailableTimeSlots([]);
    }
  }, [formData.date, bookings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name || !formData.country || !formData.whatsapp || !formData.email || 
        !formData.plan || !formData.broker || !formData.account || !formData.server || 
        !formData.balance || !formData.date || !formData.time) {
      alert('Please fill in all required fields');
      return;
    }

    // Update bookings
    const updatedBookings = { ...bookings };
    if (!updatedBookings[formData.date]) {
      updatedBookings[formData.date] = [];
    }
    updatedBookings[formData.date].push(formData.time);

    // Save to localStorage
    localStorage.setItem("kasangoBookings", JSON.stringify(updatedBookings));
    setBookings(updatedBookings);

    // Success message
    alert(`✅ Success!\n\nYour Kasango System Check has been booked for:\n${formData.date} at ${formData.time}\n\nWe'll contact you on WhatsApp: ${formData.whatsapp}`);

    // Reset form
    setFormData({
      name: '',
      country: '',
      whatsapp: '',
      email: '',
      plan: '',
      broker: '',
      platform: 'MT4',
      account: '',
      server: '',
      balance: '',
      type: 'Real',
      market: 'Gold',
      kasango: 'Yes',
      kolt: 'Yes',
      lastTrade: '',
      date: '',
      time: ''
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-zinc-900 to-black border-b border-gray-200 shadow-lg">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <button 
            onClick={() => navigate('/')}
            className="text-sm text-gray-400 hover:text-white mb-4 flex items-center gap-2 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </button>
          <h1 className="text-3xl font-light text-white mb-2">Kasango System Registration</h1>
          <p className="text-gray-400">
            Complete your registration and book your system check appointment
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          
          {/* Client Details */}
          <div className="p-8 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
              Client Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Full Name *"
                required
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder="Country *"
                required
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <input
                type="text"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handleChange}
                placeholder="WhatsApp Number *"
                required
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email *"
                required
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Kasango Plan */}
          <div className="p-8 border-b border-gray-200 bg-gray-50">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                </svg>
              </div>
              Kasango Plan
            </h3>
            <select
              name="plan"
              value={formData.plan}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">Select Plan *</option>
              <option value="AI">Kasango AI</option>
              <option value="AI Pro">Kasango AI Pro</option>
            </select>
          </div>

          {/* Trading Account */}
          <div className="p-8 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                </svg>
              </div>
              Trading Account
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                name="broker"
                value={formData.broker}
                onChange={handleChange}
                placeholder="Forex Broker *"
                required
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <select
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="MT4">MT4</option>
                <option value="MT5">MT5</option>
              </select>
              <input
                type="text"
                name="account"
                value={formData.account}
                onChange={handleChange}
                placeholder="Account Number *"
                required
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <input
                type="text"
                name="server"
                value={formData.server}
                onChange={handleChange}
                placeholder="Server *"
                required
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <input
                type="number"
                name="balance"
                value={formData.balance}
                onChange={handleChange}
                placeholder="Account Balance *"
                required
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="Real">Real</option>
                <option value="Demo">Demo</option>
                <option value="Prop Firm">Prop Firm</option>
              </select>
              <select
                name="market"
                value={formData.market}
                onChange={handleChange}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white md:col-span-2"
              >
                <option value="Gold">Gold</option>
                <option value="NAS100">NAS100</option>
                <option value="GBPJPY">GBPJPY</option>
                <option value="S&P500">S&P500</option>
              </select>
            </div>
          </div>

          {/* System Status */}
          <div className="p-8 border-b border-gray-200 bg-gray-50">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              System Status
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kasango Connection</label>
                <select
                  name="kasango"
                  value={formData.kasango}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="Yes">✓ Kasango Connected</option>
                  <option value="No">✗ Not Connected</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kolt AI Status</label>
                <select
                  name="kolt"
                  value={formData.kolt}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="Yes">✓ Kolt AI Running</option>
                  <option value="No">✗ Not Running</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Trade Date (if any)</label>
                <input
                  type="date"
                  name="lastTrade"
                  value={formData.lastTrade}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Booking */}
          <div className="p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
              </div>
              Book System Check Appointment
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Date *</label>
                <select
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">Select Date</option>
                  <option value="2026-01-10">10 Jan 2026 (Friday)</option>
                  <option value="2026-01-11">11 Jan 2026 (Saturday)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Limited slots available - Book now!</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Time *</label>
                <select
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                  disabled={!formData.date}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100"
                >
                  <option value="">Select Time</option>
                  {availableTimeSlots.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
                {!formData.date && (
                  <p className="text-xs text-gray-500 mt-1">Please select a date first</p>
                )}
                {formData.date && availableTimeSlots.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">No available slots for this date</p>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="p-8 bg-gradient-to-r from-blue-600 to-blue-700">
            <button
              type="submit"
              className="w-full py-4 bg-white hover:bg-gray-100 text-blue-600 font-bold text-lg rounded-xl transition-all hover:scale-105 shadow-lg"
            >
              Submit & Book Appointment
            </button>
            <p className="text-center text-white text-sm mt-3">
              * All fields marked with asterisk are required
            </p>
          </div>

        </form>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white py-6">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-400">
            Need help? Contact support: <a href="https://wa.me/27685358462" className="text-green-400 hover:underline">WhatsApp</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
